import { useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { LargeIconCard } from '@juvare/common-web-ui/Cards/LargeIconCard';
import { cardColors } from '@juvare/common-web-ui/Cards/cardColors';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import emptyContentImageSmall from '@juvare/common-web-ui/assets/emptyContentImageSmall.svg';
import { formatDate } from '@juvare/common-web-ui/utils';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Box, Button, ButtonBase, CircularProgress, FormControlLabel, Grid, Icon, Switch, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import { parseISO } from 'date-fns';
import React from 'react';
import { useAlertUiTranslation } from '../../../../hooks';
import { useAlertsTable } from '../../hooks';
import { buildStringUsingTemplate } from '../AlertDialog/Utils';
import { AlertIcon } from '../AlertIcon/AlertIcon';
import { AlertsList } from '../AlertsList';
import { ChipMenu } from '../AlertsList/components/ChipMenu';
import { ChannelIcon } from '../ChannelIcon';
export const TemplateCards = (props) => {
    const { t } = useAlertUiTranslation();
    const filterConfig = useFilterConfig((builder) => {
        const search = builder.quickSearchFilter({ dataPath: 'searchText', label: t('common.search') });
        const createdBy = builder.quickFilterSelect({
            dataPath: 'createdByMe',
            label: t('common.createdBy'),
            values: [
                { label: t('pages.tables.filters.anyone') },
                {
                    value: true,
                    label: t('pages.tables.filters.me'),
                },
            ],
            widget: 'form.radio',
        });
        const notificationType = builder.quickFilterMultiSelect({
            dataPath: 'notificationType',
            label: t('common.notificationType'),
            widget: 'form.multiSelect',
            valueProviderType: 'classifier.messageType',
        });
        const sharedWith = builder.quickFilterMultiSelect({
            dataPath: 'sharedWith',
            label: t('common.sharedWith.title'),
            widget: 'form.multiSelect',
            values: [
                { label: t('common.sharedWith.user'), value: 'user' },
                {
                    label: t('common.sharedWith.anyone'),
                    value: 'anyone',
                },
                { label: t('common.sharedWith.owner'), value: 'owner' },
                { label: t('common.sharedWith.not-shared'), value: 'not-shared' },
            ],
        });
        builder.allFiltersPanel({
            children: [search, createdBy, notificationType, sharedWith],
        });
    }, [t]);
    const { dataTableProps, filtersBarProps, appliedFilterData } = useAlertsTable({
        staticFilterData: { statuses: ['template'] },
        filterConfig,
        initialFilterData: props.initialFilterData,
    });
    return (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', p: 5, gap: 4 } },
        React.createElement(Typography, { variant: 'h1' }, t('common.templateCardsHeader')),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
            React.createElement(Box, { sx: { display: 'flex', justifyContent: 'space-between' } },
                React.createElement(FormControlLabel, { label: t('common.showPinnedOnly'), control: React.createElement(Switch, { checked: !!appliedFilterData.pinned, onChange: (_event, checked) => {
                            filtersBarProps.onFilter(Object.assign(Object.assign({}, appliedFilterData), { pinned: checked ? true : undefined }));
                        } }) }),
                React.createElement(ChipMenu, { label: dataTableProps.sortColumnDesc ? t('common.newestFirst') : t('common.oldestFirst'), menuItems: [
                        {
                            label: t('common.newestFirst'),
                            onClick: () => { var _a; return (_a = dataTableProps.onSortColumnChange) === null || _a === void 0 ? void 0 : _a.call(dataTableProps, 'sendDateTime', true); },
                        },
                        {
                            label: t('common.oldestFirst'),
                            onClick: () => { var _a; return (_a = dataTableProps.onSortColumnChange) === null || _a === void 0 ? void 0 : _a.call(dataTableProps, 'sendDateTime', false); },
                        },
                    ] })),
            React.createElement(AlertsList, Object.assign({}, dataTableProps, { content: React.createElement(Content, { dataTableProps: dataTableProps, noAlertsLabel: t('common.noResultsFound'), onAlertCreate: props.onAlertCreate, onReviewAndSendClick: props.onReviewAndSendClick, templateVariables: props.templateVariables }) })))));
};
const defaultTemplatesPerPageCount = 20;
const Content = ({ dataTableProps, noAlertsLabel, onAlertCreate, onReviewAndSendClick, templateVariables }) => {
    const { t } = useAlertUiTranslation();
    const [rowsPerPage, setRowsPerPage] = React.useState(defaultTemplatesPerPageCount);
    const [showShowMoreButton, setShowShowMoreButton] = React.useState(false);
    const handleLoadMoreTemplates = React.useCallback(() => {
        var _a;
        if (dataTableProps.filteredRowCount && rowsPerPage < dataTableProps.filteredRowCount) {
            const newRowsCount = rowsPerPage + defaultTemplatesPerPageCount;
            setRowsPerPage(newRowsCount);
            (_a = dataTableProps.onRowsPerPagePageChange) === null || _a === void 0 ? void 0 : _a.call(dataTableProps, newRowsCount);
        }
    }, [dataTableProps, rowsPerPage]);
    React.useEffect(() => {
        if (dataTableProps.filteredRowCount) {
            if (dataTableProps.filteredRowCount > rowsPerPage) {
                setShowShowMoreButton(true);
            }
            else if (rowsPerPage >= dataTableProps.filteredRowCount && !dataTableProps.loading) {
                setShowShowMoreButton(false);
            }
        }
    }, [dataTableProps.filteredRowCount, dataTableProps.loading, rowsPerPage]);
    if (dataTableProps.rows.length > 0) {
        return (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 } },
            React.createElement(Grid, { container: true, spacing: 3, columnSpacing: 3 },
                React.createElement(Grid, { key: 0, size: {
                        xs: 12,
                        md: 6,
                        lg: 4,
                    } },
                    React.createElement(ButtonBase, { sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignContent: 'center',
                            border: '1px solid ' + themeColors.border.main,
                            borderRadius: 1,
                            height: {
                                xs: 264,
                                sm: 254,
                                md: '100%',
                            },
                            width: '100%',
                            gap: 2,
                            '&:hover': {
                                boxShadow: '0px 1px 10px 0px rgba(0, 0, 0, 0.12), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 2px 4px 0px rgba(0, 0, 0, 0.20)',
                            },
                        }, focusRipple: true, onClick: () => onAlertCreate(), "data-test": 'new-notification-button' },
                        React.createElement(SvgIcon, { sx: {
                                padding: 1,
                                fontSize: 40,
                                borderRadius: '50%',
                                border: '1px solid ' + themeColors.border.main,
                            } },
                            React.createElement(AddIcon, null)),
                        React.createElement(Typography, { variant: 'h6', sx: { alignSelf: 'center' } }, t('common.newNotification')))),
                dataTableProps.rows.map((alert, index) => {
                    var _a, _b;
                    return (React.createElement(Grid, { key: index + 1, "data-test": `template-${index + 1}`, size: {
                            xs: 12,
                            md: 6,
                            lg: 4,
                        } },
                        React.createElement(LargeIconCard, { key: index, icon: React.createElement(AlertIcon, { icon: alert.icon, sx: {
                                    color: alert.color ? cardColors[alert.color] : 'grey',
                                } }), color: ((_a = alert.color) !== null && _a !== void 0 ? _a : 'grey'), header: React.createElement(Box, { sx: { display: 'flex', justifyContent: 'flex-end', px: 0.75, mx: 2 } },
                                React.createElement(Icon, { sx: { color: 'white' } }, alert.pinned ? React.createElement(PushPinIcon, null) : React.createElement(PushPinOutlinedIcon, null))), sx: {
                                '&:hover': {
                                    boxShadow: '0px 1px 10px 0px rgba(0, 0, 0, 0.12), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 2px 4px 0px rgba(0, 0, 0, 0.20)',
                                },
                            } },
                            React.createElement(Box, { sx: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    p: 2,
                                    gap: 2,
                                } },
                                React.createElement(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        flexGrow: 1,
                                    } },
                                    React.createElement(Typography, { variant: 'h6', sx: {
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        } },
                                        alert.title && templateVariables
                                            ? buildStringUsingTemplate(alert.title, templateVariables)
                                            : alert.title,
                                        ' - ',
                                        alert.subject && templateVariables
                                            ? buildStringUsingTemplate(alert.subject, templateVariables)
                                            : alert.subject),
                                    React.createElement(Typography, { sx: {
                                            color: themeColors.text.subtle,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        } }, (_b = alert.channels) === null || _b === void 0 ? void 0 : _b.map((channel) => (React.createElement(ChannelIcon, { key: channel, channel: channel, tooltip: true, sx: {
                                            fontSize: 20,
                                            verticalAlign: 'middle',
                                            ml: 1,
                                            '&:first-of-type': { ml: 0 },
                                        } })))),
                                    React.createElement(Typography, { variant: 'caption', sx: { color: themeColors.text.subtle } }, t('common.templateCardData', {
                                        date: alert.updated ? formatDate(parseISO(alert.updated)) : '-',
                                        name: alert.updatedBy,
                                    }))),
                                React.createElement(Box, { sx: { display: 'flex', flexDirection: 'row-reverse', gap: 2 } },
                                    React.createElement(Button, { variant: 'text', onClick: () => {
                                            onReviewAndSendClick(alert.id);
                                        }, "data-test": 'review-and-send-button' }, t('common.reviewAndSend')),
                                    React.createElement(Button, { variant: 'text', onClick: () => {
                                            onAlertCreate(alert.id);
                                        }, "data-test": 'open-button' }, t('common.open')))))));
                })),
            showShowMoreButton && (React.createElement(Button, { disabled: dataTableProps.loading, sx: {
                    alignSelf: 'center',
                    width: { xs: '100%', sm: 'auto' },
                }, variant: 'outlined', "data-test": 'show-more-button', startIcon: dataTableProps.loading ? React.createElement(CircularProgress, { size: 16 }) : React.createElement(KeyboardArrowDownIcon, null), onClick: handleLoadMoreTemplates }, t('common.showMore')))));
    }
    else if (dataTableProps.rows.length === 0 && dataTableProps.loading) {
        return (React.createElement(Box, { sx: {
                py: '140px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
            } },
            React.createElement(CircularProgress, { size: 54 })));
    }
    else if (dataTableProps.rows.length === 0 && !dataTableProps.loading) {
        return (React.createElement(Box, { sx: {
                py: '120px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
            } },
            React.createElement("img", { src: emptyContentImageSmall, alt: '', style: { maxWidth: '100%' } }),
            React.createElement(Typography, { variant: 'subtitle1' }, noAlertsLabel),
            React.createElement(Button, { color: 'primary', variant: 'contained', onClick: () => onAlertCreate() }, t('common.createNewNotification'))));
    }
    else {
        return null;
    }
};
